/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * System Visual Model Generator
 * 
 * Generates SystemVisualModel from recommendation options.
 * This is the missing link between the engine's computed recommendations
 * and the UI's visualization layer.
 * 
 * Key responsibilities:
 * - Map boiler/water types to component topology
 * - Classify thermal behavior patterns
 * - Calculate capacity signals
 * - Generate human-readable characteristics
 */

import type {
  SystemVisualModel,
  ComponentTopology,
  ThermalProfile,
  CapacitySignals,
  SystemComponent,
  SystemType,
  BurnPattern,
  DHWDeliveryMethod,
  ComfortStability,
  ComponentRole,
  ComponentLocation,
  FuelType,
  RetrofitLevel,
  LowTempReadiness,
} from './types';
import { RULESET_VERSION } from './rulesetVersion';

/**
 * Generate a visual model for a heating system recommendation
 */
export function generateVisualModel(
  optionId: string,
  systemType: SystemType,
  boilerType: string,
  waterType: string,
  heatLossKw?: number,
  flowRateLpm?: number,
  retrofitLevel?: RetrofitLevel,
  lowTempReadiness?: LowTempReadiness
): SystemVisualModel {
  const topology = generateComponentTopology(systemType, boilerType, waterType, heatLossKw);
  const thermalProfile = generateThermalProfile(
    systemType,
    boilerType,
    waterType,
    retrofitLevel,
    lowTempReadiness
  );
  const capacitySignals = generateCapacitySignals(systemType, waterType, flowRateLpm);
  const characteristics = generateCharacteristics(systemType, boilerType, waterType, thermalProfile);

  return {
    optionId,
    systemType,
    boilerType,
    waterType,
    topology,
    thermalProfile,
    capacitySignals,
    characteristics,
    generated: {
      timestamp: new Date().toISOString(),
      rulesetVersion: RULESET_VERSION,
    },
  };
}

/**
 * Generate component topology based on system configuration
 */
function generateComponentTopology(
  systemType: SystemType,
  boilerType: string,
  waterType: string,
  heatLossKw?: number
): ComponentTopology {
  const components: SystemComponent[] = [];
  let primaryHeatSource: string;
  let hotWaterStorage: string | undefined;
  let coldWaterSupply: string | undefined;
  let expansion: string | undefined;
  let systemCategory: ComponentTopology['systemCategory'];
  let hydraulicConfig: ComponentTopology['hydraulicConfig'];

  // Normalize types for consistent matching
  const normalizedBoiler = boilerType.toLowerCase();
  const normalizedWater = waterType.toLowerCase();
  const normalizedSystem = systemType.toLowerCase();

  // Determine if heat pump
  const isHeatPump = normalizedSystem.indexOf('heat_pump') >= 0 || 
                     normalizedSystem.indexOf('ashp') >= 0 || 
                     normalizedBoiler.indexOf('heat_pump') >= 0 ||
                     normalizedBoiler.indexOf('ashp') >= 0;

  if (isHeatPump) {
    // Heat Pump System
    primaryHeatSource = 'heat_pump';
    components.push({
      id: 'heat_pump',
      type: 'heat_pump',
      label: 'Air Source Heat Pump',
      location: 'external',
      role: 'heat_source',
      properties: {
        outputKw: heatLossKw ? Math.ceil(heatLossKw * 1.1) : undefined,
        fuelType: 'electricity',
      },
    });

    // Heat pumps always have a cylinder
    hotWaterStorage = 'hp_cylinder';
    components.push({
      id: 'hp_cylinder',
      type: 'cylinder',
      label: 'Heat Pump Cylinder',
      location: 'airing_cupboard',
      role: 'heat_storage',
      properties: {
        capacityLitres: 250,
        stratified: true,
        pressureType: 'mains',
      },
    });

    // Optional buffer tank
    components.push({
      id: 'buffer_tank',
      type: 'buffer',
      label: 'Buffer Tank',
      location: 'airing_cupboard',
      role: 'buffer',
      optional: true,
      properties: {
        capacityLitres: 100,
      },
    });

    systemCategory = 'heat_pump';
    hydraulicConfig = 'thermal_store';

  } else if (normalizedBoiler.indexOf('combi') >= 0) {
    // Combi Boiler System
    primaryHeatSource = 'combi_boiler';
    components.push({
      id: 'combi_boiler',
      type: 'boiler',
      label: 'Combi Boiler',
      location: 'kitchen',
      role: 'heat_source',
      properties: {
        outputKw: heatLossKw,
        fuelType: 'natural_gas',
      },
    });

    systemCategory = 'combination';
    hydraulicConfig = 'combination';

  } else if (normalizedBoiler.indexOf('system') >= 0) {
    // System Boiler
    primaryHeatSource = 'system_boiler';
    components.push({
      id: 'system_boiler',
      type: 'boiler',
      label: 'System Boiler',
      location: 'kitchen',
      role: 'heat_source',
      properties: {
        outputKw: heatLossKw,
        fuelType: 'natural_gas',
      },
    });

    // Determine cylinder type from water config
    if (normalizedWater.indexOf('unvented') >= 0) {
      hotWaterStorage = 'unvented_cylinder';
      components.push({
        id: 'unvented_cylinder',
        type: 'cylinder',
        label: 'Unvented Cylinder',
        location: 'airing_cupboard',
        role: 'heat_storage',
        properties: {
          capacityLitres: 210,
          stratified: normalizedWater.indexOf('mixergy') >= 0,
          pressureType: 'mains',
        },
      });
    } else {
      // Assume open vented
      hotWaterStorage = 'vented_cylinder';
      components.push({
        id: 'vented_cylinder',
        type: 'cylinder',
        label: 'Vented Cylinder',
        location: 'airing_cupboard',
        role: 'heat_storage',
        properties: {
          capacityLitres: 150,
          pressureType: 'gravity',
        },
      });

      coldWaterSupply = 'cws_tank';
      components.push({
        id: 'cws_tank',
        type: 'tank',
        label: 'Cold Water Tank',
        location: 'loft',
        role: 'cold_water_storage',
        properties: {
          capacityLitres: 230,
        },
      });
    }

    systemCategory = 'sealed';
    hydraulicConfig = 'S-plan';

  } else if (normalizedBoiler.indexOf('regular') >= 0) {
    // Regular Boiler
    primaryHeatSource = 'regular_boiler';
    components.push({
      id: 'regular_boiler',
      type: 'boiler',
      label: 'Regular Boiler',
      location: 'kitchen',
      role: 'heat_source',
      properties: {
        outputKw: heatLossKw,
        fuelType: 'natural_gas',
      },
    });

    // Determine cylinder type from water config (regular can be vented OR unvented)
    if (normalizedWater.indexOf('unvented') >= 0) {
      // Unvented cylinder - sealed system, no CWS
      hotWaterStorage = 'unvented_cylinder';
      components.push({
        id: 'unvented_cylinder',
        type: 'cylinder',
        label: 'Unvented Cylinder',
        location: 'airing_cupboard',
        role: 'heat_storage',
        properties: {
          capacityLitres: 210,
          stratified: normalizedWater.indexOf('mixergy') >= 0,
          pressureType: 'mains',
        },
      });

      // Feed and expansion tank (still needed for heating circuit)
      expansion = 'fe_tank';
      components.push({
        id: 'fe_tank',
        type: 'tank',
        label: 'F&E Tank',
        location: 'loft',
        role: 'expansion',
        properties: {
          capacityLitres: 15,
        },
      });

      systemCategory = 'sealed';
      hydraulicConfig = 'S-plan';

    } else {
      // Open vented cylinder - needs CWS + F&E
      hotWaterStorage = 'vented_cylinder';
      components.push({
        id: 'vented_cylinder',
        type: 'cylinder',
        label: 'Vented Cylinder',
        location: 'airing_cupboard',
        role: 'heat_storage',
        properties: {
          capacityLitres: 150,
          pressureType: 'gravity',
        },
      });

      // Cold water storage tank
      coldWaterSupply = 'cws_tank';
      components.push({
        id: 'cws_tank',
        type: 'tank',
        label: 'Cold Water Tank',
        location: 'loft',
        role: 'cold_water_storage',
        properties: {
          capacityLitres: 230,
        },
      });

      // Feed and expansion tank
      expansion = 'fe_tank';
      components.push({
        id: 'fe_tank',
        type: 'tank',
        label: 'F&E Tank',
        location: 'loft',
        role: 'expansion',
        properties: {
          capacityLitres: 15,
        },
      });

      systemCategory = 'open_vented';
      hydraulicConfig = 'S-plan';
    }

  } else {
    // Fallback for unknown types
    primaryHeatSource = 'boiler';
    components.push({
      id: 'boiler',
      type: 'boiler',
      label: 'Boiler',
      location: 'kitchen',
      role: 'heat_source',
      properties: {
        outputKw: heatLossKw,
        fuelType: 'natural_gas',
      },
    });

    systemCategory = 'sealed';
    hydraulicConfig = 'S-plan';
  }

  return {
    components,
    primaryHeatSource,
    hotWaterStorage,
    coldWaterSupply,
    expansion,
    systemCategory,
    hydraulicConfig,
  };
}

/**
 * Generate thermal behavior profile
 */
function generateThermalProfile(
  systemType: SystemType,
  boilerType: string,
  waterType: string,
  retrofitLevel?: RetrofitLevel,
  lowTempReadiness?: LowTempReadiness
): ThermalProfile {
  const normalizedBoiler = boilerType.toLowerCase();
  const normalizedWater = waterType.toLowerCase();
  const normalizedSystem = systemType.toLowerCase();

  const isHeatPump = normalizedSystem.indexOf('heat_pump') >= 0 || 
                     normalizedSystem.indexOf('ashp') >= 0 || 
                     normalizedBoiler.indexOf('heat_pump') >= 0 ||
                     normalizedBoiler.indexOf('ashp') >= 0;

  let burnPattern: BurnPattern;
  let dhwDelivery: DHWDeliveryMethod;
  let comfortStability: ComfortStability;
  let spaceHeatingResponseMinutes: number;
  let dhwResponseSeconds: number;
  let canModulate: boolean;
  let typicalCyclesPerHour: number | undefined;

  if (isHeatPump) {
    // Heat pump - continuous modulating operation
    burnPattern = 'continuous_modulating';
    dhwDelivery = 'stored';
    comfortStability = 'very_high';
    
    // Base response time - adjusted by retrofit and emitter readiness
    // For heat pumps, response time is on hours scale, not minutes
    let baseResponseMinutes = 240; // 4 hours for medium readiness (typical)
    
    if (lowTempReadiness === "high") {
      // Well-suited emitters + good retrofit -> faster response (but still hours)
      baseResponseMinutes = 180; // 3 hours
    } else if (lowTempReadiness === "low") {
      // Poor emitters or poor retrofit -> much slower (many hours)
      baseResponseMinutes = 480; // 8 hours base for low readiness
    }
    
    // Further adjust for retrofit level
    if (retrofitLevel === "low") {
      // Poor insulation means longer to heat, more heat loss
      // Low retrofit + low readiness can take 12-24 hours
      baseResponseMinutes = Math.round(baseResponseMinutes * 1.5);
    } else if (retrofitLevel === "high") {
      // Good insulation means quicker to stabilize
      baseResponseMinutes = Math.round(baseResponseMinutes * 0.8);
    }
    
    spaceHeatingResponseMinutes = baseResponseMinutes;
    dhwResponseSeconds = 5; // Stored, so quick
    canModulate = true;
    typicalCyclesPerHour = 0.5; // Rarely cycles

  } else if (normalizedBoiler.indexOf('combi') >= 0) {
    // Combi - instant heat on demand
    burnPattern = 'instant';
    dhwDelivery = 'instant';
    comfortStability = 'medium';
    
    // Combi response less sensitive to retrofit, but still affected
    let baseResponseMinutes = 15;
    if (retrofitLevel === "low") {
      baseResponseMinutes = 20; // Poor envelope -> more cycling, slower stabilization
    } else if (retrofitLevel === "high") {
      baseResponseMinutes = 12; // Good envelope -> quicker to stabilize
    }
    
    spaceHeatingResponseMinutes = baseResponseMinutes;
    dhwResponseSeconds = 30; // Wait for boiler to heat exchanger
    canModulate = true;
    typicalCyclesPerHour = 3;

  } else if (normalizedBoiler.indexOf('system') >= 0 || normalizedBoiler.indexOf('regular') >= 0) {
    // System/Regular boiler with stored hot water
    burnPattern = 'long_modulating';
    
    // Check if stratified cylinder (Mixergy)
    if (normalizedWater.indexOf('mixergy') >= 0) {
      dhwDelivery = 'top_down';
      comfortStability = 'very_high';
    } else {
      dhwDelivery = 'stored';
      comfortStability = 'high';
    }
    
    // System boiler response moderately affected by retrofit
    let baseResponseMinutes = 20;
    if (retrofitLevel === "low") {
      baseResponseMinutes = 25;
    } else if (retrofitLevel === "high") {
      baseResponseMinutes = 15;
    }
    
    spaceHeatingResponseMinutes = baseResponseMinutes;
    dhwResponseSeconds = 5; // Stored, so quick
    canModulate = true;
    typicalCyclesPerHour = 2;

  } else {
    // Fallback for unknown types
    burnPattern = 'medium_cycling';
    dhwDelivery = 'stored';
    comfortStability = 'medium';
    spaceHeatingResponseMinutes = 20;
    dhwResponseSeconds = 10;
    canModulate = false;
    typicalCyclesPerHour = 4;
  }

  return {
    burnPattern,
    dhwDelivery,
    comfortStability,
    spaceHeatingResponseMinutes,
    dhwResponseSeconds,
    canModulate,
    typicalCyclesPerHour,
  };
}

/**
 * Generate capacity signals
 */
function generateCapacitySignals(
  systemType: SystemType,
  waterType: string,
  flowRateLpm?: number
): CapacitySignals {
  const normalizedWater = waterType.toLowerCase();
  const normalizedSystem = systemType.toLowerCase();

  const isCombi = normalizedSystem.indexOf('combi') >= 0;
  const isUnvented = normalizedWater.indexOf('unvented') >= 0;
  const isHeatPump = normalizedSystem.indexOf('heat_pump') >= 0 || normalizedSystem.indexOf('ashp') >= 0;

  let simultaneousOutlets: number;
  let bathsWithoutWaiting: number;
  let recoveryTimeMinutes: number | 'instant';
  let peakFlowLpm: number | undefined;
  let suitableForPowerShower: boolean;

  if (isCombi) {
    // Combi - limited by flow rate
    simultaneousOutlets = 1;
    bathsWithoutWaiting = 0; // Fills slowly
    recoveryTimeMinutes = 'instant'; // No tank to recover
    peakFlowLpm = flowRateLpm || 12; // Typical combi flow
    suitableForPowerShower = false;

  } else if (isUnvented || isHeatPump) {
    // Unvented or heat pump - high pressure, good capacity
    simultaneousOutlets = 2;
    bathsWithoutWaiting = 1;
    recoveryTimeMinutes = isHeatPump ? 120 : 60; // Heat pump slower
    peakFlowLpm = flowRateLpm || 25;
    suitableForPowerShower = true;

  } else {
    // Open vented - gravity pressure
    simultaneousOutlets = 1;
    bathsWithoutWaiting = 1;
    recoveryTimeMinutes = 45;
    peakFlowLpm = flowRateLpm || 15;
    suitableForPowerShower = false;
  }

  return {
    simultaneousOutlets,
    bathsWithoutWaiting,
    recoveryTimeMinutes,
    peakFlowLpm,
    suitableForPowerShower,
  };
}

/**
 * Generate human-readable characteristics
 */
function generateCharacteristics(
  systemType: SystemType,
  boilerType: string,
  waterType: string,
  thermalProfile: ThermalProfile
): SystemVisualModel['characteristics'] {
  const normalizedBoiler = boilerType.toLowerCase();
  const normalizedWater = waterType.toLowerCase();
  const normalizedSystem = systemType.toLowerCase();

  const isHeatPump = normalizedSystem.indexOf('heat_pump') >= 0 || 
                     normalizedSystem.indexOf('ashp') >= 0 || 
                     normalizedBoiler.indexOf('heat_pump') >= 0;
  const isCombi = normalizedBoiler.indexOf('combi') >= 0;
  const isMixergy = normalizedWater.indexOf('mixergy') >= 0;

  let behaviorSummary: string;
  let keyDifferentiator: string;
  let primaryBenefit: string;
  let primaryTradeoff: string;

  if (isHeatPump) {
    behaviorSummary = 'Continuous low-temperature heat delivery with excellent efficiency';
    keyDifferentiator = 'Runs constantly at low power rather than in bursts';
    primaryBenefit = 'Lowest running costs and carbon emissions';
    primaryTradeoff = 'Higher upfront cost and requires suitable radiators';

  } else if (isCombi) {
    behaviorSummary = 'Instant heat on demand with no storage needed';
    keyDifferentiator = 'No hot water cylinder - heats water as you use it';
    primaryBenefit = 'Minimal space requirements and unlimited hot water supply';
    primaryTradeoff = 'Limited flow rate - one outlet at a time';

  } else if (isMixergy) {
    behaviorSummary = 'Smart stratified storage maintains constant temperature';
    keyDifferentiator = 'Top-down draw preserves hot water temperature throughout use';
    primaryBenefit = 'Excellent hot water consistency and rapid recharge';
    primaryTradeoff = 'Premium cylinder cost';

  } else if (normalizedBoiler.indexOf('system') >= 0) {
    if (normalizedWater.indexOf('unvented') >= 0) {
      behaviorSummary = 'Mains-pressure stored hot water with long modulating burns';
      keyDifferentiator = 'High pressure at multiple outlets simultaneously';
      primaryBenefit = 'Excellent shower performance and hot water capacity';
      primaryTradeoff = 'Requires annual servicing of pressure vessel';
    } else {
      behaviorSummary = 'Stored hot water system with gravity-fed pressure';
      keyDifferentiator = 'Simple, proven technology with stored capacity';
      primaryBenefit = 'Reliable hot water delivery and easy to service';
      primaryTradeoff = 'Lower pressure than mains-fed systems';
    }

  } else if (normalizedBoiler.indexOf('regular') >= 0) {
    behaviorSummary = 'Traditional open vented system with gravity circulation';
    keyDifferentiator = 'Open system with loft tanks - no pressure vessel';
    primaryBenefit = 'Simple, maintainable, and forgiving of water quality issues';
    primaryTradeoff = 'Gravity pressure limits shower performance';

  } else {
    // Fallback
    behaviorSummary = 'Standard gas boiler system with stored hot water';
    keyDifferentiator = 'Balanced approach to comfort and efficiency';
    primaryBenefit = 'Proven technology with good hot water capacity';
    primaryTradeoff = 'Moderate efficiency compared to heat pumps';
  }

  return {
    behaviorSummary,
    keyDifferentiator,
    primaryBenefit,
    primaryTradeoff,
  };
}
